import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';

import type { W_EquipmentUtilization_FindBySubCategoryTable_VM } from 'ab-api/web/equipmentUtilization/findBySubCategoryTable/viewModel';

import CLIENT from 'af-constants/routes/client';

import Table from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import DollarCell from 'af-components/Table/Cells/DollarCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import type { TableProps } from 'af-components/Table/types';

import styles from './styles.module.scss';
import WorkingCell from '../../UtilizationShared/WorkingCellNew';

interface OwnProps {
	tableName: string;
	orgAlias: string;
	companyName: string;
	redirectTo: (url: string) => void;
	hasSearchInput: TableProps<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>['hasSearchInput'];
	searchLabel: TableProps<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>['searchLabel'];
	fetch: TableProps<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>['fetch'];
	buttons?: TableProps<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>['buttons'];
	tableRef: React.RefObject<Nullable<TableRef<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>>>;
}

type Props = OwnProps;

const _renderDollarAmountCell = () => (cell: CellContext<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0], number>) => {
	return (
		<DollarCell
			className={styles['revenue-cell']}
			isUnitPrice={true}
			maxFractionDigits={2}
			value={cell.getValue()}
		/>
	);
};

const _renderWorkingCell = (cell: CellContext<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0], {
	daysAssigned: number;
	daysAvailable: number;
	daysUnavailable: number;
	totalDays: number;
}>) => {
	return (
		<WorkingCell
			daysAssigned={cell.row.original.daysAssigned}
			daysAvailable={cell.row.original.daysAvailable}
			daysUnavailable={cell.row.original.daysUnavailable}
			totalDays={cell.row.original.totalDays}
		/>
	);
};

const EquipmentUtilizationTable: React.FC<Props> = (props) => {
	const {
		tableName,
		redirectTo,
		orgAlias,
		companyName,
		buttons,
		fetch,
		searchLabel,
		hasSearchInput,
		tableRef,
	} = props;

	const goToEquipmentStatusHistory = React.useCallback((id: number) => {
		redirectTo(CLIENT.COMPANY.EQUIPMENT.STATUS_HISTORY(id.toString(), orgAlias, companyName));
	}, [companyName, orgAlias, redirectTo]);

	const goToEquipmentPreview = React.useCallback((id: number) => {
		redirectTo(CLIENT.COMPANY.EQUIPMENT.PREVIEW(id.toString(), orgAlias, companyName));
	}, [companyName, orgAlias, redirectTo]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0], unknown>) => {
		const actions = [
			{ onClick: () => goToEquipmentPreview(_cell.row.original.id), label: 'Preview' },
			{ onClick: () => goToEquipmentStatusHistory(_cell.row.original.id), label: 'Status History' },
		];
		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [goToEquipmentPreview, goToEquipmentStatusHistory]);

	const columns: TableProps<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>['columns'] = React.useMemo(() => [
		{
			id: 'name',
			header: 'Equipment ID',
			accessor: 'name',
			enableSorting: true,
			cell: (cell: CellContext<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0], string>) => (
				<span>
					<strong>{cell.row.original.name}</strong> {cell.row.original.specification ? <span className="small-label m-l-s">{cell.row.original.specification}</span> : null}
				</span>
			),
		},
		{
			id: 'timePeriod',
			header: 'Year',
			accessor: 'timePeriod',
			enableSorting: true,
			cell: (cell: CellContext<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0], number>) => cell.getValue() ?? <EmptyCell />,
		},
		{
			id: 'daysAssigned',
			header: 'Working',
			accessor: 'daysAssigned',
			enableSorting: true,
			cell: _renderWorkingCell,
		},
		{
			id: 'dailyRevenue',
			header: 'Daily Revenue',
			accessor: 'dailyRevenue',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
		{
			id: 'totalRevenue',
			header: 'Total revenue',
			accessor: 'totalRevenue',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
		{
			id: 'dailyCost',
			header: 'Daily Cost',
			accessor: 'dailyCost',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
		{
			id: 'totalCost',
			header: 'Total Cost',
			accessor: 'totalCost',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
		{
			id: 'targetProfit',
			header: 'Target Profit/Loss',
			accessor: 'targetProfit',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [resolveActionsButton]);

	const onRowClick = React.useCallback((row: Row<W_EquipmentUtilization_FindBySubCategoryTable_VM['items'][0]>) => {
		if (row?.id) {
			goToEquipmentPreview(row.original.id);
		}
	}, [goToEquipmentPreview]);

	return (
		<Table
			buttons={buttons}
			columns={columns}
			fetch={fetch}
			hasSearchInput={hasSearchInput}
			hasSubRows={false}
			onRowClick={onRowClick}
			ref={tableRef}
			searchLabel={searchLabel}
			selectable={true}
			tableName={tableName}
		/>
	);
};

export default React.memo(EquipmentUtilizationTable);
